// src/api/order.ts — Order lifecycle, customer, address search, and key functions

import { Customer, Order, Payment } from "square";
import { OrderItem, getLineItemsFromOrderItems } from "../types/OrderItem";
import { ApiResult } from "../types/ApiResult";
import {
  AddressSearchItem,
  GeoapifyAutocompleteResponse,
} from "../types/AddressSearchItem";
import { getPayment, cancelPayment, refundPayment } from "./payment";
import { cancelPrintfulOrder } from "./printful";
import { sendAdminAlert } from "./admin";
import { ReturnRequest } from "./customer";
import { getOrderAttribution } from "../lib/attribution";

// ---------------------------------------------------------------------------
// API Keys
// ---------------------------------------------------------------------------

/** Fetch the HERE Maps API key from the serverless function. */
export const getHereKey = async (): Promise<ApiResult<{ hereKey: string }>> => {
  try {
    const response = await fetch(`/.netlify/functions/get-here-key`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
    });
    return response.json();
  } catch (error) {
    console.error("Error getting HERE key:", error);
    return { error: `Error getting HERE key: ${error}` };
  }
};

/** Fetch the Geoapify API key from the serverless function. */
export const getGeoapifyKey = async (): Promise<
  ApiResult<{ geoapifyKey: string }>
> => {
  try {
    const response = await fetch(`/.netlify/functions/get-geoapify-key`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
    });
    return response.json();
  } catch (error) {
    console.error("Error getting Geoapify key:", error);
    return { error: `Error getting Geoapify key: ${error}` };
  }
};

// ---------------------------------------------------------------------------
// Order CRUD
// ---------------------------------------------------------------------------

/** Fetch a single order by ID.
 *  Access requires proof of ownership: either the buyer `email` the order was
 *  placed with (guest tracking) or an `authToken` whose customer owns the order. */
export const getOrder = async (
  orderId: string,
  opts?: { email?: string; authToken?: string },
): Promise<ApiResult<Order>> => {
  if (!orderId) return null;
  try {
    const params = new URLSearchParams({ id: orderId });
    if (opts?.email) params.set("email", opts.email);
    const response = await fetch(
      `/.netlify/functions/get-order?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(process.env.REACT_APP_FUNCTION_SECRET
            ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
            : {}),
          ...(opts?.authToken
            ? { Authorization: `Bearer ${opts.authToken}` }
            : {}),
        },
      },
    );
    return response.json();
  } catch (error) {
    console.error("Error getting order:", error);
    return { error: `Error getting order: ${error}` };
  }
};

/**
 * Validate a promo code against the server (code, active flag, date window and
 * both redemption caps).
 *
 * `email` is the buyer identity a code with a per-customer limit is counted
 * against. It's optional because the promo box sits in the order summary and is
 * reachable before the Contact step: without it the server SKIPS the
 * per-customer check rather than refusing, and create-order re-resolves with the
 * real email as the authoritative word. PaymentFormPage re-validates once an
 * email is entered so an "already used" code can't silently survive to checkout.
 */
export const validatePromo = async (
  code: string,
  email?: string,
): Promise<
  ApiResult<{
    valid: boolean;
    discountType?: "percentage" | "fixed";
    discountPercentage?: string | null;
    /** Fixed dollar-off amount in integer cents (only for `fixed` codes). */
    discountAmountCents?: number | null;
    displayName?: string;
    code?: string;
    reason?: string;
  }>
> => {
  try {
    const response = await fetch("/.netlify/functions/validate-promo", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({ code, email: email || undefined }),
    });
    return response.json();
  } catch (error) {
    console.error("Error validating promo code:", error);
    return { error: `Error validating promo code: ${error}` };
  }
};

/** Create a new Square order for checkout. */
export const createOrder = async (
  customerId: string,
  orderItems: OrderItem[],
  contactInfo: Payment,
  donationAmount: number,
  shippingAmount?: number,
  taxAmount?: number,
  promoCode?: string,
  idempotencyKey?: string,
): Promise<ApiResult<Order>> => {
  if (!customerId || !orderItems || orderItems.length <= 0) return null;

  const lineItems = getLineItemsFromOrderItems(orderItems);

  try {
    const response = await fetch("/.netlify/functions/create-order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({
        customerId,
        lineItems,
        contactInfo,
        donationAmount,
        shippingAmount: shippingAmount ?? 0,
        taxAmount: taxAmount ?? 0,
        promoCode: promoCode ?? undefined,
        // Acquisition source, read straight from localStorage rather than
        // threaded through every caller as a 9th positional argument. Undefined
        // when nothing was captured (storage unavailable) — the server treats a
        // missing source as "unattributed" rather than guessing.
        attribution: getOrderAttribution() ?? undefined,
        // Per-attempt key so a retried checkout creates a fresh order instead of
        // resolving to a prior (possibly canceled) one. Falls back server-side
        // to a body hash when omitted.
        idempotencyKey: idempotencyKey ?? undefined,
      }),
    });
    return response.json();
  } catch (error) {
    console.error("Error creating order:", error);
    return { error: `Error creating order: ${error}` };
  }
};

/**
 * Submit a return/refund request for selected items from an existing order.
 * Records the request in Netlify Blobs (no Square return order is created) and
 * emails the customer a confirmation. Returns the stored request record.
 */
export const createReturnOrder = async (
  originalOrder: Order,
  orderItems: OrderItem[],
  reason?: string,
  notes?: string,
  authToken?: string,
  email?: string,
): Promise<ApiResult<ReturnRequest>> => {
  if (!originalOrder || !orderItems || orderItems.length <= 0) return null;

  const returnItems = getLineItemsFromOrderItems(orderItems);

  try {
    const response = await fetch("/.netlify/functions/create-return-order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({
        originalOrder,
        returnItems,
        reason,
        notes,
        email,
      }),
    });
    return response.json();
  } catch (error) {
    console.error("Error creating return order:", error);
    return { error: `Error creating return order: ${error}` };
  }
};

/** Cancel an existing order (marks it as CANCELED in Square). */
export const cancelOrder = async (
  order: Order,
  authToken?: string,
): Promise<ApiResult<Order>> => {
  if (!order) return null;
  try {
    const response = await fetch("/.netlify/functions/cancel-order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ order }),
    });
    return response.json();
  } catch (error) {
    console.error("Error cancelling order:", error);
    return { error: `Error cancelling order: ${error}` };
  }
};

/**
 * Full cancel flow: checks Printful production status (for captured payments),
 * then cancels or refunds the payment and cancels the order. Throws descriptive
 * errors on any failure so the calling component can display them to the user.
 */
export const cancelAll = async (
  orderId: string,
  cancellationReason?: string,
  authToken?: string,
  email?: string,
  options?: {
    /** Skip the "Your Order Has Been Canceled" email. Used by checkout-failure
     *  cleanup (voiding a payment for an order the customer never saw
     *  confirmed) where that email would only confuse the buyer; customer-
     *  initiated cancellations should keep the default (send it). */
    suppressCancellationEmail?: boolean;
  },
) => {
  // 1. Get Order — getOrder is ownership-gated, so pass the auth token (logged
  //    in) or the buyer email (guest) so the lookup is authorized.
  const getOrderResponse = await getOrder(orderId, { email, authToken });
  if (!getOrderResponse?.data || !!getOrderResponse.error) {
    throw new Error("Cannot find order, please try again.");
  }

  const paymentId = getOrderResponse.data.tenders?.[0]?.paymentId;
  if (!paymentId) return;

  // 2. Get Payment — thread auth so the ownership check passes.
  const getPaymentResponse = await getPayment(paymentId, { authToken, email });
  const payment = getPaymentResponse?.data;
  if (!payment || !!getPaymentResponse?.error) {
    throw new Error(
      "Could not find a payment for this order, please try again.",
    );
  }

  // 3. Cancel or Refund the Payment depending on current status.
  //    Square reports an authorized-but-uncaptured (delayed-capture) payment as
  //    "APPROVED"; accept "AUTHORIZED" too defensively. This is now the PRIMARY
  //    cancellation path (orders sit authorized through the free-cancellation
  //    window before finalize-orders captures them), so it must match reality —
  //    in the old 5s-capture flow this branch was rarely hit because payments
  //    were already COMPLETED by the time anyone cancelled.
  if (payment.status === "APPROVED" || payment.status === "AUTHORIZED") {
    // Pre-capture cancellation — this is the FREE path (voiding an authorized
    // payment incurs no Square processing fee). A Printful DRAFT was created at
    // checkout (before capture), so delete it first to avoid leaving an orphan
    // that finalize-orders could later confirm. Best-effort: a draft that's
    // missing (notFound) or a transient Printful error must not block the void —
    // the customer not being charged is what matters. inProduction can't happen
    // here (drafts only enter production at finalize, after capture).
    try {
      const printfulResult = await cancelPrintfulOrder(orderId);
      if (printfulResult?.error) {
        void sendAdminAlert(
          `⚠️ Printful Draft Delete Failed (pre-capture cancel) — ${orderId}`,
          "A customer cancelled before capture; the payment was voided but the Printful draft could not be deleted. Delete it manually in Printful.",
          { "Square Order ID": orderId, Error: String(printfulResult.error) },
        );
      }
    } catch (printfulErr: any) {
      void sendAdminAlert(
        `⚠️ Printful Draft Delete Threw (pre-capture cancel) — ${orderId}`,
        "A customer cancelled before capture; deleting the Printful draft threw. Verify it's removed in Printful.",
        {
          "Square Order ID": orderId,
          Error: String(printfulErr?.message ?? printfulErr),
        },
      );
    }

    const canceledPaymentResponse = await cancelPayment(paymentId, authToken);
    if (!canceledPaymentResponse?.data || !!canceledPaymentResponse?.error) {
      throw new Error("Could not cancel the payment, please try again.");
    }
  } else if (payment.status === "COMPLETED") {
    // 4. Pre-flight: check whether Printful has started production.
    //    Printful orders are created after payment capture, so they only exist
    //    for COMPLETED payments. Cancel the Printful order first so we know it
    //    is still possible before touching the Square payment.
    let printfulCancelled = false;
    try {
      const printfulResult = await cancelPrintfulOrder(orderId);
      if (printfulResult?.inProduction) {
        throw new Error(
          "This order can no longer be canceled because your custom item is already being made. If you need help, please email support@americanaandcompany.com.",
        );
      }
      if (printfulResult?.error) {
        throw new Error(printfulResult.error);
      }
      printfulCancelled = true;
    } catch (printfulErr: any) {
      // Re-throw the in-production block directly to the caller.
      if ((printfulErr?.message as string)?.includes("already being made")) {
        throw printfulErr;
      }
      // Any other Printful error: proceed with Square refund and alert admin.
      void sendAdminAlert(
        `⚠️ Printful Pre-flight Cancel Failed — ${orderId}`,
        "Printful cancellation check failed before Square refund. Manual review may be needed.",
        {
          "Square Order ID": orderId,
          Error: String(printfulErr?.message ?? printfulErr),
        },
      );
    }

    // 5. Refund the Square payment
    const refundedPaymentResponse = await refundPayment(
      paymentId,
      payment.amountMoney?.amount ?? BigInt(0),
      cancellationReason,
      authToken,
    );
    if (!refundedPaymentResponse?.data || !!refundedPaymentResponse?.error) {
      throw new Error("Could not refund the payment, please try again.");
    }

    // 6. Cancel the Square order after successful refund
    const getOrderResponse2 = await getOrder(orderId, { email, authToken });
    if (!getOrderResponse2?.data || !!getOrderResponse2?.error) {
      throw new Error("Cannot find order to cancel, please try again.");
    }
    const cancelOrderResponse = await cancelOrder(
      getOrderResponse2.data,
      authToken,
    );
    if (!cancelOrderResponse?.data || !!cancelOrderResponse?.error) {
      throw new Error("Could not cancel the order, please try again.");
    }

    // 7. Alert admin if the Printful order was not confirmed as cancelled
    //    (notFound is acceptable — it means no Printful order was ever created).
    if (!printfulCancelled) {
      void sendAdminAlert(
        `⚠️ Printful Order Could Not Be Cancelled — ${orderId}`,
        "A customer cancelled their Square order and the payment was refunded, but the Printful order could not be cancelled automatically. Manual cancellation in Printful may be needed.",
        { "Square Order ID": orderId },
      );
    }
  } else {
    throw new Error(
      "Payment is in an unexpected state and cannot be cancelled.",
    );
  }

  // Cancellation succeeded (either path). Send the customer a confirmation for
  // their records. Best-effort: a failed email must never turn a successful
  // cancellation/refund into a user-facing error, so swallow any rejection.
  if (!options?.suppressCancellationEmail) {
    try {
      await sendCancellationEmail(orderId);
    } catch (emailErr) {
      console.error("Cancellation email failed (non-fatal):", emailErr);
    }
  }
};

/**
 * Undo a Square order whose checkout blew up after `create-order` but before the
 * customer ever saw a confirmation.
 *
 * Usually `create-payment` failed — a decline, a network drop, a function 500, a
 * timeout. `createPayment` swallows fetch rejections and returns `{ error }`, so
 * the caller cannot tell a clean decline from a lost response, and a lost
 * response may mean Square authorized the card without us ever seeing it. Assume
 * it did: an un-voided authorization holds the customer's funds for the ~6 days
 * Square takes to auto-void, while the retry they're about to make authorizes a
 * second time.
 *
 * Two steps, because neither alone is sufficient:
 *   1. `cancelAll` voids an APPROVED payment. It returns early when the order
 *      has no tender (the common case — `create-payment` never reached Square)
 *      and throws on a FAILED one, so it never cancels the ORDER here.
 *   2. `cancelOrder` is what actually closes the order. (`cancelAll`'s COMPLETED
 *      branch does cancel the order itself, making this a no-op second cancel —
 *      tolerated, and unreachable at checkout, where payments are authorize-only.)
 *
 * Best-effort and independent: `cleanup-orders` reconciles whatever is left, and
 * neither failure may mask the original checkout error. Never throws.
 *
 * `suppressCancellationEmail` — the customer never saw this order confirmed, so
 * a "Your Order Has Been Canceled" email out of nowhere is pure confusion.
 */
export const cleanupFailedCheckout = async (
  order: Order,
  opts?: { authToken?: string; email?: string },
): Promise<void> => {
  if (!order?.id) return;
  try {
    await cancelAll(order.id, undefined, opts?.authToken, opts?.email, {
      suppressCancellationEmail: true,
    });
  } catch {
    // No live payment to void, or the void failed. Close the order regardless.
  }
  try {
    await cancelOrder(order, opts?.authToken);
  } catch (cancelErr) {
    console.error("Checkout cleanup: order cancel failed:", cancelErr);
  }
};

/**
 * Send the order-cancellation confirmation email. The server re-reads the
 * recipient, items, and refund amount from Square, so the client only supplies
 * the orderId. Best-effort: callers should not let a failure here surface as a
 * cancellation failure (the order is already canceled by this point).
 */
export const sendCancellationEmail = async (
  orderId: string,
): Promise<ApiResult<string | null>> => {
  if (!orderId) return null;
  try {
    const response = await fetch(
      "/.netlify/functions/send-cancellation-email",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(process.env.REACT_APP_FUNCTION_SECRET
            ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
            : {}),
        },
        body: JSON.stringify({ orderId }),
      },
    );
    return response.json();
  } catch (error) {
    console.error("Error sending cancellation email:", error);
    return { error: `Error sending cancellation email: ${error}` };
  }
};

/** Flag an order for manual review (e.g. suspected fraud). */
export const flagOrder = async (order: Order): Promise<ApiResult<Order>> => {
  try {
    const response = await fetch("/.netlify/functions/flag-order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({ order }),
    });
    return response.json();
  } catch (error) {
    console.error("Error flagging order:", error);
    return { error: `Error flagging order: ${error}` };
  }
};

// ---------------------------------------------------------------------------
// Customer & Email
// ---------------------------------------------------------------------------

/** Upsert a Square customer record for the given contact info.
 *
 *  Pass the session's `authToken` when the buyer is signed in: the server then
 *  updates THAT account rather than whichever record the typed email happens to
 *  match, which is what keeps the order, the payment and the saved card all on
 *  one customer id. */
export const upsertCustomer = async (
  contactInfo: Payment,
  phone?: string,
  authToken?: string,
): Promise<ApiResult<Customer>> => {
  if (!contactInfo?.buyerEmailAddress) return null;
  try {
    const response = await fetch("/.netlify/functions/upsert-customer", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ contactInfo, phone }),
    });
    // `await` here, not a bare `return response.json()`: returning the promise
    // hands the rejection to the CALLER, past this catch. handlePay deliberately
    // treats a FAILED upsert as survivable for a signed-in buyer — it falls back
    // to `session.customerId` rather than losing the sale over an enrichment
    // that's only there to prefill the next checkout. That fallback branches on
    // an `{ error }` result, so a Netlify 502/504 (an HTML body) or a function
    // timeout would reject straight past it into the outer catch and kill the
    // checkout it was written to protect. Same fix as verifyCustomerOtp.
    return await response.json();
  } catch (error) {
    console.error("Error upserting customer:", error);
    return { error: `Error upserting customer: ${error}` };
  }
};

/** Authoritative cost breakdown (integer cents) for the confirmation receipt,
 *  derived from the Square order so the emailed numbers reconcile exactly. */
export interface OrderCostBreakdown {
  subtotal: number; // product line items, pre-discount
  discount?: number; // promo discount (positive cents)
  promoLabel?: string; // e.g. "AMERICA250" — shown as the discount row label
  donation?: number; // charitable donation line item
  shipping?: number; // shipping service charge
  tax?: number; // sales tax
  total: number; // grand total
}

/** Send an order confirmation email via the serverless email function. */
export const sendEmailConfirmation = async (
  orderId: string,
  totalAmount: number,
  orderItems: OrderItem[],
  contactInfo: Payment,
  /** Estimated delivery window (Printful min/max delivery dates, YYYY-MM-DD). */
  deliveryEstimate?: { minDeliveryDate?: string; maxDeliveryDate?: string },
  /** Line-item cost breakdown so the receipt shows tax/shipping/promo, not just the total. */
  costBreakdown?: OrderCostBreakdown,
): Promise<ApiResult<string>> => {
  if (totalAmount <= 0) return null;
  if (!contactInfo?.buyerEmailAddress) return null;
  if (!orderItems || orderItems.length <= 0) return null;

  const lineItems = getLineItemsFromOrderItems(orderItems);

  try {
    const response = await fetch("/.netlify/functions/send-email-order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(process.env.REACT_APP_FUNCTION_SECRET
          ? { "x-api-key": process.env.REACT_APP_FUNCTION_SECRET }
          : {}),
      },
      body: JSON.stringify({
        orderId,
        totalAmount,
        lineItems,
        contactInfo,
        minDeliveryDate: deliveryEstimate?.minDeliveryDate,
        maxDeliveryDate: deliveryEstimate?.maxDeliveryDate,
        costBreakdown,
      }),
    });
    return response.json();
  } catch (error) {
    console.error("Error sending order email:", error);
    return { error: `Error sending order email: ${error}` };
  }
};

// ---------------------------------------------------------------------------
// Address Autocomplete
// ---------------------------------------------------------------------------

/** Autocomplete an address using the HERE Maps API. */
export const searchHereAddress = async (
  query: string,
  key: string,
): Promise<AddressSearchItem[]> => {
  if (!query || query.length <= 4) return [];
  const url = `https://autocomplete.search.hereapi.com/v1/autocomplete?q=${query}&in=countryCode:USA&apiKey=${key}`;
  try {
    const response = await fetch(url);
    const data = await response.json();
    return data.items as AddressSearchItem[];
  } catch (error) {
    console.error("Error fetching HERE address:", error);
    return [];
  }
};

/** Autocomplete an address using the Geoapify Geocoding API. */
export const searchGeoapifyAddress = async (
  query: string,
  key: string,
  countrycode: string = "us",
): Promise<GeoapifyAutocompleteResponse | null> => {
  if (!query || query.trim().length < 3) return null;
  const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
    query,
  )}&limit=6&filter=countrycode:${countrycode}&apiKey=${key}`;
  try {
    const response = await fetch(url);
    return response.json();
  } catch (error) {
    console.error("Error fetching Geoapify address:", error);
    return null;
  }
};
